import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { LopTabBar } from './LopTabBar';
import { StatusFooter } from '../StatusFooter/StatusFooter';
import { LopAssistantChat } from '../LopAssistantChat';
import { usePageViewTracker } from '../../hooks/usePageViewTracker';
import { TabsProvider, TabRouterSync, TabHost, LopTabSwitcher, LopTabsCapModal } from '../../tabs';
import { ToastProvider } from '../ErrorToast';
import './AppShell.css';

const STORAGE_KEY = 'lop.sidebar.pinned';

function loadPinned(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'boolean' ? parsed : true;
  } catch {
    return true;
  }
}

// AppShell is mounted as `path="/*"` (see App.tsx) with NO nested <Route>
// children — internal tabs (fund-valuation-infra/plans/internal-tabs.md) need
// AppShell to own its own per-tab `<Routes>` matching (TabHost), one per open
// tab, instead of a single `<Outlet/>` driven by the outer router. TabsProvider
// wraps everything so TopBar/Sidebar/LopTabBar/TabHost/TabRouterSync can all
// read the same tabs state via useTabs().
export function AppShell() {
  const [isPinned, setIsPinned] = useState<boolean>(() => loadPinned());

  // Screen-usage analytics — records (debounced) every route change for the
  // current user. Runs only when authenticated (the hook bails on null user
  // and AppShell sits behind ProtectedRoute), so we never count login-screen
  // traffic. See src/hooks/usePageViewTracker.ts. Reads the REAL router
  // location (AppShell renders above any per-tab UNSAFE_LocationContext
  // override), so tab switches count as views too — intentional, see plan §4.
  usePageViewTracker();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isPinned));
    } catch {
      // storage unavailable; ignore
    }
  }, [isPinned]);

  function togglePin() {
    setIsPinned((prev) => !prev);
  }

  // Global "jump to sidebar search" shortcut. The physical key (event.code) is
  // used so it fires regardless of the OS keyboard language: KeyG = g/ע, KeyE =
  // e/ק. Only when NOT editing a cell / typing in a field. Pins the sidebar (the
  // search renders only when pinned) then focuses the search input.
  //
  // The other global shortcut, Alt+Q (the internal-tabs MRU switcher, plan
  // §3.5), is registered separately by <LopTabSwitcher/> below (mounted once,
  // inside TabsProvider so it can read tabs/mru) — it self-attaches its own
  // keydown/keyup/blur listeners via useTabSwitcher() rather than living
  // inline here, since (unlike g/e) it deliberately has NO isTypingTarget
  // guard and must keep working while a field or an AgGrid cell is focused.
  const pendingSearchFocus = useRef(false);

  const focusSearch = useCallback(() => {
    const sel = '.sidebar__search-input';
    const now = document.querySelector<HTMLInputElement>(sel);
    if (now) {
      now.focus();
      now.select();
      return;
    }
    // Not yet mounted (sidebar just pinned) — focus on the next frame.
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(sel);
      if (input) {
        input.focus();
        input.select();
      }
    });
  }, []);

  useEffect(() => {
    function isTypingTarget(el: Element | null): boolean {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      if (el.closest('.ag-cell-edit-wrapper')) return true; // AgGrid inline editor
      return false;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyG' && e.code !== 'KeyE') return;
      if (isTypingTarget(document.activeElement)) return;
      if (document.querySelector('.ag-cell-inline-editing')) return;
      e.preventDefault();
      if (isPinned) {
        focusSearch();
      } else {
        pendingSearchFocus.current = true;
        setIsPinned(true);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPinned, focusSearch]);

  // After the sidebar pins (search now mounted), honor a pending focus request.
  useEffect(() => {
    if (isPinned && pendingSearchFocus.current) {
      pendingSearchFocus.current = false;
      focusSearch();
    }
  }, [isPinned, focusSearch]);

  return (
    <ToastProvider>
    <TabsProvider>
      <TabRouterSync />
      <LopTabSwitcher />
      <LopTabsCapModal />
      <div className="app-shell" data-sidebar-pinned={isPinned ? 'true' : 'false'}>
        <TopBar />
        <LopTabBar />
        <div className="app-shell__body">
          <Sidebar isPinned={isPinned} onTogglePin={togglePin} />
          <main className="app-shell__content">
            <TabHost />
          </main>
        </div>
        <StatusFooter />
        <LopAssistantChat />
      </div>
    </TabsProvider>
    </ToastProvider>
  );
}
